import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Megaphone, Pin, X, Plus, Trash2, Edit2, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface Announcement {
  id: string;
  league_id: string;
  author_id: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

const LeagueAnnouncementBanner = () => {
  const { activeLeague } = useLeague();
  const { user } = useAuth();
  const { isCommissioner } = useRole();
  const { toast } = useToast();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!activeLeague?.id) return;

    const fetchAnnouncements = async () => {
      const { data } = await supabase
        .from('league_announcements')
        .select('*')
        .eq('league_id', activeLeague.id)
        .eq('is_pinned', true)
        .order('created_at', { ascending: false })
        .limit(5);

      setAnnouncements((data as Announcement[]) || []);
      setLoading(false);
    };

    fetchAnnouncements();
  }, [activeLeague?.id]);

  const handleCreate = async () => {
    if (!activeLeague || !user || !newContent.trim()) return;
    setSaving(true);

    const { error } = await supabase
      .from('league_announcements')
      .insert({
        league_id: activeLeague.id,
        author_id: user.id,
        content: newContent.trim(),
        is_pinned: true,
      } as any);

    if (error) {
      toast({ title: 'Failed to post', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Announcement posted' });
      setNewContent('');
      setComposing(false);

      // Re-fetch
      const { data } = await supabase
        .from('league_announcements')
        .select('*')
        .eq('league_id', activeLeague.id)
        .eq('is_pinned', true)
        .order('created_at', { ascending: false })
        .limit(5);
      setAnnouncements((data as Announcement[]) || []);
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('league_announcements')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    } else {
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Announcement removed' });
    }
  };

  const handleUnpin = async (id: string) => {
    const { error } = await supabase
      .from('league_announcements')
      .update({ is_pinned: false } as any)
      .eq('id', id);

    if (!error) {
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Announcement unpinned' });
    }
  };

  const handleEdit = async (id: string) => {
    if (!editContent.trim()) return;
    const { error } = await supabase
      .from('league_announcements')
      .update({ content: editContent.trim(), updated_at: new Date().toISOString() } as any)
      .eq('id', id);

    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    } else {
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, content: editContent.trim() } : a));
      setEditingId(null);
      toast({ title: 'Announcement updated' });
    }
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedIds.has(a.id));

  if (loading || (!isCommissioner && visibleAnnouncements.length === 0)) return null;

  return (
    <div className="space-y-2">
      {/* Pinned announcements */}
      {visibleAnnouncements.map(a => (
        <Card key={a.id} className="border-primary/30 bg-primary/5">
          <CardContent className="p-3 flex items-start gap-3">
            <Pin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {editingId === a.id ? (
                <div className="flex gap-2">
                  <Textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="text-sm min-h-[60px]"
                    autoFocus
                  />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(a.id)}>
                      <Check className="w-3.5 h-3.5 text-primary" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingId(null)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{a.content}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    📌 Pinned · {format(new Date(a.created_at), 'MMM d, h:mm a')}
                  </p>
                </>
              )}
            </div>
            {isCommissioner && editingId !== a.id && (
              <div className="flex items-center gap-0.5 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingId(a.id); setEditContent(a.content); }}>
                  <Edit2 className="w-3 h-3 text-muted-foreground" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleUnpin(a.id)}>
                  <Pin className="w-3 h-3 text-muted-foreground" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(a.id)}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            )}
            {!isCommissioner && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => setDismissedIds(prev => new Set([...prev, a.id]))}
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Commissioner compose */}
      {isCommissioner && (
        <>
          {composing ? (
            <Card className="border-border">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <Megaphone className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">New Announcement</span>
                </div>
                <Textarea
                  placeholder="Write your announcement..."
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  className="min-h-[80px] text-sm"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setComposing(false); setNewContent(''); }}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleCreate} disabled={saving || !newContent.trim()} className="gap-1">
                    <Pin className="w-3.5 h-3.5" />
                    {saving ? 'Posting...' : 'Pin Announcement'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 text-xs border-dashed border-primary/30 text-primary hover:bg-primary/5"
              onClick={() => setComposing(true)}
            >
              <Plus className="w-3.5 h-3.5" />
              Pin an Announcement
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default LeagueAnnouncementBanner;
